/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';
import { api, type AccountProfile } from '@/lib/api';

interface AuthState { user: User | null; profile: AccountProfile | null; loading: boolean; error: string | null; logout: () => Promise<void>; refreshProfile: () => Promise<void> }
const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshProfile = async () => {
    const result = await api.me();
    setProfile(result.data);
  };

  useEffect(() => onAuthStateChanged(firebaseAuth, async (nextUser) => {
    setUser(nextUser); setProfile(null); setError(null);
    if (nextUser) {
      // Stay in the loading state while the profile loads, so the "Account access
      // unavailable" screen never flashes during the brief /me fetch after sign-in.
      setLoading(true);
      try { await refreshProfile(); }
      catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to load account'); }
    }
    setLoading(false);
  }), []);

  const logout = async () => { await signOut(firebaseAuth); setProfile(null); };
  return <AuthContext.Provider value={{ user, profile, loading, error, logout, refreshProfile }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
